import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import LocationPicker from '../components/LocationPicker';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';
import { CreditCard, MapPin, User, Mail, Phone, Tag } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutPage = () => {
  const { cart, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pincodeValid, setPincodeValid] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    delivery_address: '',
    pincode: '',
    discount_code: '',
  });

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    const addressWithCoords = formData.delivery_address 
      ? `${formData.delivery_address} (Lat: ${location.lat.toFixed(6)}, Lng: ${location.lng.toFixed(6)})`
      : `Lat: ${location.lat.toFixed(6)}, Lng: ${location.lng.toFixed(6)}`;
    setFormData({ ...formData, delivery_address: addressWithCoords });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validatePincode = async () => {
    if (!formData.pincode || formData.pincode.length !== 6) {
      toast.error('Please enter a valid 6-digit pincode');
      return;
    }

    try {
      const response = await axios.post(`${API}/validate-pincode`, {
        pincode: formData.pincode,
      });
      setPincodeValid(response.data.valid);
      if (response.data.valid) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Error validating pincode');
    }
  };

  const applyDiscount = async () => {
    if (!formData.discount_code) {
      toast.error('Please enter a discount code');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/validate-discount?code=${formData.discount_code}&subtotal=${getCartTotal()}`
      );
      setDiscountApplied(response.data);
      toast.success(`Discount applied! Save ₹${response.data.discount_amount}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid discount code');
      setDiscountApplied(null);
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!pincodeValid) {
      toast.error('Please validate your delivery pincode first');
      return;
    }

    setLoading(true);

    try {
      const items = cart.map((item) => ({
        product_id: item.id,
        quantity: item.quantity,
      }));

      const orderResponse = await axios.post(`${API}/create-order`, {
        ...formData,
        items,
      });

      const { order_id, razorpay_order_id, amount, currency, key_id } = orderResponse.data;

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error('Payment gateway failed to load. Please try again.');
        setLoading(false);
        return;
      }

      const options = {
        key: key_id,
        amount: amount * 100,
        currency: currency,
        name: 'GreenPure Biofuel',
        description: 'Premium Biomass Briquettes',
        order_id: razorpay_order_id,
        handler: async function (response) {
          try {
            await axios.post(`${API}/verify-payment`, {
              order_id: order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            
            clearCart();
            toast.success('Payment successful!');
            navigate(`/order-success/${order_id}`);
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: formData.customer_name,
          email: formData.customer_email,
          contact: formData.customer_phone,
        },
        theme: {
          color: '#a3e635',
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            toast.error('Payment cancelled');
          },
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
      setLoading(false);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order');
      setLoading(false);
    }
  };

  const subtotal = getCartTotal();
  const discount = discountApplied ? discountApplied.discount_amount : 0;
  const subtotal_after_discount = subtotal - discount;
  const gst = Math.round(subtotal_after_discount * 0.05);
  const total = subtotal_after_discount + gst;

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      <Navbar />

      <div className="container mx-auto px-4 md:px-6 max-w-7xl py-16">
        <h1 className="text-6xl md:text-8xl font-bold tracking-tighter uppercase leading-[0.9] mb-8 font-heading">
          <span className="text-lime-400">Checkout</span>
        </h1>

        <form onSubmit={handlePayment}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-zinc-900 border border-zinc-800 p-6">
                <h2 className="font-heading text-2xl font-bold uppercase mb-6 flex items-center gap-2">
                  <User className="w-6 h-6 text-lime-400" />
                  Customer Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                      className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                      <Mail className="inline w-4 h-4 mr-1" /> Email *
                    </label>
                    <input
                      type="email"
                      name="customer_email"
                      value={formData.customer_email}
                      onChange={handleChange}
                      required
                      className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none"
                      data-testid="input-email"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                      <Phone className="inline w-4 h-4 mr-1" /> Phone *
                    </label>
                    <input
                      type="tel"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10}"
                      placeholder="10-digit mobile number"
                      className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none"
                      data-testid="input-phone"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 p-6">
                <h2 className="font-heading text-2xl font-bold uppercase mb-6 flex items-center gap-2 text-lime-400">
                  <MapPin className="w-6 h-6" />
                  Delivery Address
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                      Address *
                    </label>
                    <textarea
                      name="delivery_address"
                      value={formData.delivery_address}
                      onChange={handleChange}
                      required
                      rows={3}
                      className="w-full bg-zinc-900 border-zinc-800 focus:border-lime-400 text-lg px-4 py-3 text-white border outline-none"
                      data-testid="input-address"
                    />
                  </div>

                  <div className="border-t border-zinc-800 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowMap(!showMap)}
                      className="w-full bg-lime-400/10 border border-lime-400/20 text-lime-400 hover:bg-lime-400/20 font-semibold uppercase tracking-wider px-4 py-3 transition-colors flex items-center justify-center gap-2"
                      data-testid="toggle-map-btn"
                    >
                      <MapPin className="w-5 h-5" />
                      {showMap ? 'Hide Map' : 'Pin Location on Map'}
                    </button>
                  </div>

                  {showMap && (
                    <div className="border-t border-zinc-800 pt-4">
                      <LocationPicker
                        onLocationSelect={handleLocationSelect}
                        initialPosition={selectedLocation}
                      />
                    </div>
                  )}

                  <div>
                    <label className="block text-sm uppercase tracking-wider text-zinc-400 mb-2 font-semibold">
                      Pincode * (Bengaluru only)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode}
                        onChange={handleChange}
                        required
                        maxLength={6}
                        pattern="[0-9]{6}"
                        className="flex-1 bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none"
                        data-testid="input-pincode"
                      />
                      <button
                        type="button"
                        onClick={validatePincode}
                        className="bg-transparent border-2 border-zinc-700 text-white hover:border-lime-400 hover:text-lime-400 font-bold uppercase tracking-wider px-6 py-3 transition-colors"
                        data-testid="validate-pincode-btn"
                      >
                        Validate
                      </button>
                    </div>
                    {pincodeValid !== null && (
                      <p className={`mt-2 text-sm ${pincodeValid ? 'text-lime-400' : 'text-red-500'}`} data-testid="pincode-status">
                        {pincodeValid ? '✅ Delivery available!' : '❌ Delivery not available'}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-zinc-900 border border-zinc-800 p-6">
                <h2 className="font-heading text-2xl font-bold uppercase mb-6 flex items-center gap-2">
                  <Tag className="w-6 h-6 text-lime-400" />
                  Discount Code
                </h2>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="discount_code"
                    value={formData.discount_code}
                    onChange={handleChange}
                    placeholder="FIRSTBAG or MANDI25"
                    className="flex-1 bg-zinc-900 border-zinc-800 focus:border-lime-400 h-12 text-lg px-4 text-white border outline-none uppercase"
                    data-testid="input-discount-code"
                  />
                  <button
                    type="button"
                    onClick={applyDiscount}
                    className="bg-transparent border-2 border-zinc-700 text-white hover:border-lime-400 hover:text-lime-400 font-bold uppercase tracking-wider px-6 py-3 transition-colors"
                    data-testid="apply-discount-btn"
                  >
                    Apply
                  </button>
                </div>
                {discountApplied && (
                  <p className="mt-2 text-lime-400 text-sm" data-testid="discount-status">
                    ✅ {discountApplied.discount_percent}% discount applied!
                  </p>
                )}
                <p className="mt-3 text-zinc-500 text-xs">
                  Try: FIRSTBAG (₹750 for first 25kg) or MANDI25 (5% off on bulk orders)
                </p>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-zinc-900 border border-zinc-800 p-6 sticky top-24">
                <h2 className="font-heading text-2xl font-bold uppercase mb-6">Order Summary</h2>

                <div className="space-y-3 mb-6 max-h-64 overflow-y-auto">
                  {cart.map((item) => (
                    <div key={item.id} className="flex justify-between text-sm pb-3 border-b border-zinc-800">
                      <div>
                        <p className="text-white font-semibold">{item.name}</p>
                        <p className="text-zinc-400 text-xs">Qty: {item.quantity}</p>
                      </div>
                      <span className="text-white font-semibold">₹{item.price * item.quantity}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 border-t border-zinc-800 pt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Subtotal</span>
                    <span className="text-white">₹{subtotal}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-sm" data-testid="discount-amount">
                      <span className="text-lime-400">Discount</span>
                      <span className="text-lime-400">-₹{discount}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">GST (5%)</span>
                    <span className="text-white" data-testid="gst-amount">₹{gst}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Delivery</span>
                    <span className="text-lime-400">FREE</span>
                  </div>
                </div>

                <div className="border-t border-zinc-800 pt-4 mb-6 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-xl font-semibold uppercase">Total</span>
                    <span className="text-3xl font-bold text-lime-400" data-testid="checkout-total">
                      ₹{total}
                    </span>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || !pincodeValid}
                  className="w-full bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#ffffff] transition-all hover:-translate-y-1 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="place-order-btn"
                >
                  <CreditCard className="w-5 h-5" />
                  {loading ? 'Processing...' : 'Pay with UPI/Card'}
                </button>

                <div className="mt-6 p-4 bg-lime-400/10 border border-lime-400/20">
                  <p className="text-lime-400 font-semibold text-sm mb-1">✅ Secure Payment</p>
                  <p className="text-zinc-400 text-xs">UPI, Cards, NetBanking supported</p>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default CheckoutPage;
